import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export const useFavorites = () => {
  const { session } = useAuth();
  const queryClient = useQueryClient();

  const { data: favorites = [], isLoading } = useQuery({
    queryKey: ['favorites', session?.user?.id],
    queryFn: async () => {
      if (!session?.user?.id) return [];
      
      const { data, error } = await supabase
        .from('favorites')
        .select('restaurant_id')
        .eq('user_id', session.user.id);
      
      if (error) {
        console.error('Error fetching favorites:', error);
        throw error;
      }
      
      return data.map(fav => fav.restaurant_id);
    },
    enabled: !!session?.user?.id,
  });

  const { mutate: toggleFavorite } = useMutation({
    mutationFn: async (restaurantId: number) => {
      if (!session?.user?.id) {
        throw new Error('Must be logged in to favorite restaurants');
      }

      const isFavorited = favorites.includes(restaurantId);

      if (isFavorited) {
        const { error } = await supabase
          .from('favorites')
          .delete()
          .eq('user_id', session.user.id)
          .eq('restaurant_id', restaurantId);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('favorites')
          .insert({
            user_id: session.user.id,
            restaurant_id: restaurantId
          });

        if (error) throw error;
      }

      return { restaurantId, isFavorited };
    },
    onSuccess: ({ restaurantId, isFavorited }) => {
      queryClient.setQueryData(['favorites', session?.user?.id], (old: number[] = []) => {
        if (isFavorited) {
          return old.filter(id => id !== restaurantId);
        } else {
          return [...old, restaurantId];
        }
      });
      toast.success(isFavorited ? 'Removed from favorites' : 'Added to favorites');
    },
    onError: (error) => {
      console.error('Error toggling favorite:', error);
      toast.error('Failed to update favorites');
    }
  });

  return {
    favorites,
    isLoading,
    toggleFavorite,
    isFavorited: (restaurantId: number) => favorites.includes(restaurantId)
  };
};